import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { useDamageClaimForm } from './hooks/useDamageClaimForm';
import {
  DamageClaimModalProps,
  DamageType,
  DamageClaimFormData,
  IncidentLocation,
  RepairShopInfo,
} from './types';

// Step components
import InstructionsStep from './steps/shared/InstructionsStep';
import DamageTypeStep from './steps/shared/DamageTypeStep';
import SubcategoryStep from './steps/physical/SubcategoryStep';
import PhotosUploadStep from './steps/physical/PhotosUploadStep';
import AssessmentStep from './steps/physical/AssessmentStep';
import DamagedPartsStep from './steps/physical/DamagedPartsStep';
import IncidentDateTimeStep from './steps/physical/IncidentDateTimeStep';
import IncidentLocationStep from './steps/physical/IncidentLocationStep';
import DamageDescriptionStep from './steps/physical/DamageDescriptionStep';
import RepairShopInfoStep from './steps/physical/RepairShopInfoStep';
import AdditionalInfoStep from './steps/physical/AdditionalInfoStep';
import RepairEstimateStep from './steps/physical/RepairEstimateStep';
import MechanicalIsCarDriveableStep from './steps/mechanical/IsCarDriveableStep';
import MechanicalIssuesStep from './steps/mechanical/MechanicalIssuesStep';
import { toPublicS3ObjectUrl, resolveTripPhotoUrl } from '../../utils/s3PublicUrl';

const CREATE_DAMAGE_CLAIM = gql`
  mutation CreateDamageClaim($input: CreateDamageClaimInput!) {
    createDamageClaim(input: $input) {
      id
      claimNum
      status
      createdAt
    }
  }
`;

const GET_PRESIGNED_URL_FOR_DAMAGE_CLAIM = gql`
  mutation GetPresignedUrlForDamageClaim(
    $fileType: String!
    $claimId: String!
    $photoType: String!
  ) {
    getPresignedUrlForDamageClaim(fileType: $fileType, claimId: $claimId, photoType: $photoType) {
      url
      key
    }
  }
`;

function trimOrUndefined(s: string | undefined | null): string | undefined {
  const t = s?.trim();
  return t ? t : undefined;
}

function compactIncidentLocation(loc: IncidentLocation | undefined): IncidentLocation | undefined {
  if (!loc) return undefined;
  const out: IncidentLocation = {};
  const country = trimOrUndefined(loc.country);
  const streetAddress = trimOrUndefined(loc.streetAddress);
  const city = trimOrUndefined(loc.city);
  const postalCode = trimOrUndefined(loc.postalCode);
  if (country) out.country = country;
  if (streetAddress) out.streetAddress = streetAddress;
  if (city) out.city = city;
  if (postalCode) out.postalCode = postalCode;
  return Object.keys(out).length > 0 ? out : undefined;
}

function compactRepairShopInfo(info: RepairShopInfo | undefined): RepairShopInfo | undefined {
  if (!info) return undefined;
  const out: RepairShopInfo = {};
  const shopName = trimOrUndefined(info.shopName);
  const address = trimOrUndefined(info.address);
  const contactName = trimOrUndefined(info.contactName);
  const phoneNumber = trimOrUndefined(info.phoneNumber);
  if (shopName) out.shopName = shopName;
  if (address) out.address = address;
  if (contactName) out.contactName = contactName;
  if (phoneNumber) out.phoneNumber = phoneNumber;
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Parity with Flutter spec: omit empty arrays / unset fields; no photo keys for mechanical. */
function buildCreateDamageClaimInput(
  formData: DamageClaimFormData,
  beforePhotoUrls: string[],
  afterPhotoUrls: string[],
  incidentOccurredAt: number | undefined,
): Record<string, unknown> {
  const damageDescription = formData.damageDescription?.trim();
  if (!formData.damageType || !damageDescription) {
    throw new Error('Missing required claim fields');
  }

  const input: Record<string, unknown> = {
    bookingId: formData.bookingId,
    damageType: formData.damageType,
    damageDescription,
  };

  if (formData.damageType === DamageType.PHYSICAL) {
    if (!formData.physicalDamageSubcategory) {
      throw new Error('Physical damage category is required');
    }
    input.physicalDamageSubcategory = formData.physicalDamageSubcategory;
    if (beforePhotoUrls.length > 0) input.beforePhotos = beforePhotoUrls;
    input.afterPhotos = afterPhotoUrls;

    if (formData.policeReportFiled != null) input.policeReportFiled = formData.policeReportFiled;
    if (formData.waterInsideCar != null) input.waterInsideCar = formData.waterInsideCar;
    if (formData.whoWasDriving != null) input.whoWasDriving = formData.whoWasDriving;
    if (formData.anyoneInjured != null) input.anyoneInjured = formData.anyoneInjured;
    if (formData.anotherPersonInvolved != null) {
      input.anotherPersonInvolved = formData.anotherPersonInvolved;
    }
    if (formData.isCarDriveable != null) input.isCarDriveable = formData.isCarDriveable;
    if (formData.didVehicleLoseFluids != null) {
      input.didVehicleLoseFluids = formData.didVehicleLoseFluids;
    }
    if (formData.didAirbagsDeploy != null) input.didAirbagsDeploy = formData.didAirbagsDeploy;

    if (incidentOccurredAt != null) input.incidentOccurredAt = incidentOccurredAt;
    const il = compactIncidentLocation(formData.incidentLocation);
    if (il) input.incidentLocation = il;
    if (formData.damagedParts.length > 0) input.damagedParts = formData.damagedParts;
    const dpo = trimOrUndefined(formData.damagedPartsOtherDescription);
    if (dpo) input.damagedPartsOtherDescription = dpo;
    const addl = trimOrUndefined(formData.additionalInfo);
    if (addl) input.additionalInfo = addl;
    const rs = compactRepairShopInfo(formData.repairShopInfo);
    if (rs) input.repairShopInfo = rs;
    if (formData.repairEstimateRange != null) {
      input.repairEstimateRange = formData.repairEstimateRange;
    }
    return input;
  }

  // MECHANICAL — no beforePhotos/afterPhotos; no physical-only assessment fields
  if (formData.isCarDriveable != null) input.isCarDriveable = formData.isCarDriveable;
  if (formData.mechanicalIssues.length > 0) input.mechanicalIssues = formData.mechanicalIssues;
  const mio = trimOrUndefined(formData.mechanicalIssueOtherDescription);
  if (mio) input.mechanicalIssueOtherDescription = mio;
  const addl = trimOrUndefined(formData.additionalInfo);
  if (addl) input.additionalInfo = addl;
  const rs = compactRepairShopInfo(formData.repairShopInfo);
  if (rs) input.repairShopInfo = rs;

  return input;
}

const Overlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}
`;

const ModalContainer = styled.div`
  ${tw`bg-white rounded-card w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col`}
`;

const Header = styled.div`
  ${tw`flex items-center justify-between px-6 py-4 border-b border-border-default`}
`;

const HeaderLeft = styled.div`
  ${tw`flex items-center gap-3`}
`;

const BackButton = styled.button`
  ${tw`p-2 text-text-secondary hover:text-text-secondary`}
`;

const HeaderTitle = styled.h2`
  ${tw`text-lg font-semibold text-text-primary`}
`;

const CloseButton = styled.button`
  ${tw`p-2 text-text-secondary hover:text-text-secondary`}
`;

const ProgressBarContainer = styled.div`
  ${tw`w-full h-1 bg-bg-secondary`}
`;

const ProgressBar = styled.div<{ progress: number }>`
  ${tw`h-full bg-black transition-all duration-300`}
  width: ${props => props.progress}%;
`;

const Content = styled.div`
  ${tw`flex-1 overflow-y-auto p-6`}
`;

const DamageClaimModal: React.FC<DamageClaimModalProps> = ({
  isOpen,
  onClose,
  bookingId,
  guestName,
  onClaimSubmitted,
  tripStartImages,
  tripEndImages,
}) => {
  const {
    formData,
    updateFormData,
    currentStepId,
    isFirstStep,
    isLastStep,
    progress,
    goToNextStep,
    goToPreviousStep,
    validateCurrentStep,
    resetForm,
  } = useDamageClaimForm(bookingId);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tempClaimId, setTempClaimId] = useState<string | null>(null);

  const [createDamageClaim] = useMutation(CREATE_DAMAGE_CLAIM);
  const [getPresignedUrl] = useMutation(GET_PRESIGNED_URL_FOR_DAMAGE_CLAIM);

  const tripPhotoAllowlist = useMemo(() => {
    const set = new Set<string>();
    const addRows = (rows?: Array<{ url?: string | null } | null> | null) => {
      rows?.forEach(row => {
        if (row?.url) set.add(resolveTripPhotoUrl(row.url));
      });
    };
    addRows(tripStartImages);
    addRows(tripEndImages);
    return set;
  }, [tripStartImages, tripEndImages]);

  const tripPickerItems = useMemo(() => {
    const items: { key: string; resolved: string; label: string }[] = [];
    let i = 0;
    tripStartImages?.forEach(row => {
      if (row?.url) {
        items.push({
          key: `s-${i++}`,
          resolved: resolveTripPhotoUrl(row.url),
          label: 'Trip start',
        });
      }
    });
    tripEndImages?.forEach(row => {
      if (row?.url) {
        items.push({
          key: `e-${i++}`,
          resolved: resolveTripPhotoUrl(row.url),
          label: 'Trip end',
        });
      }
    });
    return items;
  }, [tripStartImages, tripEndImages]);

  const handleClose = useCallback(() => {
    resetForm();
    setTempClaimId(null);
    onClose();
  }, [resetForm, onClose]);

  const uploadImageToS3 = async (
    file: File,
    claimId: string,
    photoType: 'before' | 'after',
  ): Promise<string> => {
    const { data } = await getPresignedUrl({
      variables: {
        fileType: file.type || 'image/jpeg',
        claimId,
        photoType,
      },
    });

    const presignedUrl = data?.getPresignedUrlForDamageClaim?.url;
    const key = data?.getPresignedUrlForDamageClaim?.key;

    if (!presignedUrl || !key) {
      throw new Error('Failed to get presigned URL');
    }

    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'image/jpeg',
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload photo (${uploadResponse.status})`);
    }

    return toPublicS3ObjectUrl(key);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let beforePhotoUrls: string[] = [];
      let afterPhotoUrls: string[] = [];

      if (formData.damageType === DamageType.PHYSICAL) {
        const claimId = tempClaimId || `temp-${Date.now()}`;
        if (!tempClaimId) {
          setTempClaimId(claimId);
        }

        const collectEvidenceUrls = async (
          slots: typeof formData.beforeEvidenceSlots,
          photoType: 'before' | 'after',
        ): Promise<string[]> => {
          const out: string[] = [];
          for (const slot of slots) {
            if (slot.kind === 'trip') {
              if (!tripPhotoAllowlist.has(slot.url)) {
                throw new Error(
                  'Invalid trip photo selection. Remove it and choose again from trip photos.',
                );
              }
              out.push(slot.url);
            } else {
              out.push(await uploadImageToS3(slot.file, claimId, photoType));
            }
          }
          return out;
        };

        beforePhotoUrls = await collectEvidenceUrls(formData.beforeEvidenceSlots, 'before');
        afterPhotoUrls = await collectEvidenceUrls(formData.afterEvidenceSlots, 'after');
      }

      let incidentOccurredAt: number | undefined;
      if (formData.incidentOccurredAt) {
        const date = new Date(formData.incidentOccurredAt);
        if (formData.incidentTime) {
          const [hours, minutes] = formData.incidentTime.split(':').map(Number);
          date.setHours(hours || 0, minutes || 0);
        }
        incidentOccurredAt = date.getTime();
      }

      const input = buildCreateDamageClaimInput(
        formData,
        beforePhotoUrls,
        afterPhotoUrls,
        incidentOccurredAt,
      );

      const { data } = await createDamageClaim({
        variables: { input: input as never },
      });

      toast.success(
        `Damage claim submitted successfully. Claim number: ${data?.createDamageClaim?.claimNum}`,
      );
      onClaimSubmitted?.();
      handleClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to submit damage claim. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = useCallback(() => {
    if (!validateCurrentStep()) {
      toast.error('Please complete this step before continuing.');
      return;
    }

    if (isLastStep) {
      handleSubmit();
    } else {
      goToNextStep();
    }
  }, [validateCurrentStep, isLastStep, goToNextStep]);

  const handleBack = useCallback(() => {
    goToPreviousStep();
  }, [goToPreviousStep]);

  const renderStep = () => {
    const stepProps = {
      formData,
      updateFormData,
      onNext: handleNext,
      onBack: handleBack,
      isSubmitting,
      guestName,
      tripPickerItems,
      tripPhotoAllowlist,
    };

    switch (currentStepId) {
      case 'instructions':
        return <InstructionsStep {...stepProps} />;

      case 'damageType':
        return <DamageTypeStep {...stepProps} />;

      case 'subcategory':
        return <SubcategoryStep {...stepProps} />;

      case 'photos':
        return <PhotosUploadStep {...stepProps} />;

      case 'policeReport':
        return (
          <AssessmentStep
            {...stepProps}
            question="Was a police report filed?"
            fieldName="policeReportFiled"
          />
        );

      case 'waterInsideCar':
        return (
          <AssessmentStep
            {...stepProps}
            question="Did water get inside the car?"
            subtitle="A large amount of water can cause serious damage to electrical components. Answering this question will help us understand the severity of the damage."
            fieldName="waterInsideCar"
            showUnknown={false}
          />
        );

      case 'isCarDriveable':
        return (
          <AssessmentStep
            {...stepProps}
            question="Is the car driveable?"
            fieldName="isCarDriveable"
          />
        );

      case 'didVehicleLoseFluids':
        return (
          <AssessmentStep
            {...stepProps}
            question="Did the vehicle lose any fluids?"
            fieldName="didVehicleLoseFluids"
          />
        );

      case 'didAirbagsDeploy':
        return (
          <AssessmentStep
            {...stepProps}
            question="Did any airbags deploy?"
            fieldName="didAirbagsDeploy"
          />
        );

      case 'anyoneInjured':
        return (
          <AssessmentStep {...stepProps} question="Was anyone injured?" fieldName="anyoneInjured" />
        );

      case 'whoWasDriving':
        return (
          <AssessmentStep
            {...stepProps}
            question="Who was driving the vehicle when the incident occurred?"
            fieldName="whoWasDriving"
            isDriverQuestion
            guestName={guestName}
          />
        );

      case 'anotherPersonInvolved':
        return (
          <AssessmentStep
            {...stepProps}
            question="Was there another person involved in the incident?"
            subtitle="Providing details about the person (aka, third party) will help us better understand the incident and find a resolution."
            fieldName="anotherPersonInvolved"
          />
        );

      case 'incidentDateTime':
        return <IncidentDateTimeStep {...stepProps} />;

      case 'incidentLocation':
        return <IncidentLocationStep {...stepProps} />;

      case 'damagedParts':
        return <DamagedPartsStep {...stepProps} />;

      case 'damageDescription':
      case 'mechanicalDamageDescription':
        return <DamageDescriptionStep {...stepProps} />;

      case 'repairShopInfo':
      case 'mechanicalRepairShopInfo':
        return <RepairShopInfoStep {...stepProps} />;

      case 'additionalInfo':
      case 'mechanicalAdditionalInfo':
        return (
          <AdditionalInfoStep
            {...stepProps}
            isLastStep={currentStepId === 'mechanicalAdditionalInfo'}
          />
        );

      case 'repairEstimate':
        return <RepairEstimateStep {...stepProps} />;

      // Mechanical specific steps
      case 'mechanicalIsCarDriveable':
        return <MechanicalIsCarDriveableStep {...stepProps} />;

      case 'mechanicalIssues':
        return <MechanicalIssuesStep {...stepProps} />;

      default:
        return <InstructionsStep {...stepProps} />;
    }
  };

  const getStepTitle = (): string => {
    if (formData.damageType === DamageType.PHYSICAL) {
      return 'Physical damage';
    }
    if (formData.damageType === DamageType.MECHANICAL) {
      return 'Mechanical damage';
    }
    return 'Report damage';
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderLeft>
            {!isFirstStep && (
              <BackButton onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </BackButton>
            )}
            <HeaderTitle>{getStepTitle()}</HeaderTitle>
          </HeaderLeft>
          <CloseButton onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </CloseButton>
        </Header>

        <ProgressBarContainer>
          <ProgressBar progress={progress} />
        </ProgressBarContainer>

        <Content>{renderStep()}</Content>
      </ModalContainer>
    </Overlay>
  );
};

export default DamageClaimModal;
